
import { defineComponent, PropType, ref, Ref } from 'vue';
import { CountryModel, KeyValuePair } from '@/types/B2BcontentServerContract';
import { Form } from 'vee-validate';
import utilities from '@/shared/utilities.service';
import ProfileRequestAccessFormRenderer from '@/b2b/request-access/ui/ProfileRequestAccessFormRenderer.vue';
import useRequestAccess, {
    HeadingModel,
    InputSelectModel, InputTelModel,
    InputTextModel,
    RequestAccessFormModel,
    RequestAccessModel, TextareaModel,
} from '@/b2b/request-access/use-request-access';
import { IBlockLabels, useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import Recaptcha, { RecaptchaComponentInstance } from '@/shared/form/recaptcha/Recaptcha.vue';
import Deferred from '@/core/async/Deferred';
import siteContextStore from '@/shared/content/site-context.store';

const STORE_ID_KEY = 'X-StoreId';

export default defineComponent({
    name: 'ProfileRequestAccess',
    components: { Recaptcha, ProfileRequestAccessFormRenderer, Form },
    props: {
        companyTypes: {
            type: Array as PropType<KeyValuePair<string, string>[]>,
            required: false,
            default: () => [],
        },
        countries: {
            type: Array as PropType<CountryModel[]>,
            required: false,
            default: () => [],
        },
        simpleForm: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    setup(props) {
        const labels = useBlockLabels();
        const { requestAccess } = useRequestAccess();
        const formElements = formElementsModel(props, labels);
        const formValidation = generateFormValidation(formElements);
        const requestConfirmationTiming: number = 1500;
        const confirmationAnimationTiming: Ref<number> = ref(300);
        const success: Ref<boolean> = ref(false);
        const recaptcha: Ref<RecaptchaComponentInstance | null> = ref(null);
        const recaptchaKey: Ref<string> = ref('');
        const storeId = siteContextStore.siteHeaders.find(({ key }) => key === STORE_ID_KEY)?.value ?? undefined;
        const pending: Ref<boolean> = ref(false);

        const setSuccess = (newSuccessValue: boolean) => {
            success.value = newSuccessValue;
        };

        const verifyRecaptcha = () => {
            pending.value = true;
            recaptcha.value && recaptcha.value.reset();
            recaptcha.value && recaptcha.value.execute();
        };

        const submit = async(form: RequestAccessModel, { resetForm }): Promise<void> => {
            const deferred = new Deferred<void>();
            const animationTiming = utilities.delay(requestConfirmationTiming);
            try {
                const response = requestAccess({ ...form, storeId }, recaptchaKey.value, props.simpleForm);

                await Promise.all([
                    animationTiming,
                    response,
                ]);

                setTimeout(() => {
                    setSuccess(true);
                    resetForm();
                    pending.value = false;
                    deferred.resolve();
                }, confirmationAnimationTiming.value + 50);

                return deferred.promise;
            } catch (error) {
                pending.value = false;
                throw error;
            }
        };

        const hasErrors = (errorObj) => {
            return Object.keys(errorObj).length > 0;
        };

        return {
            labels,
            success,
            recaptcha,
            formValidation,
            confirmationAnimationTiming,
            formElements,
            hasErrors,
            setSuccess,
            submit,
            verifyRecaptcha,
            recaptchaKey,
            pending,
        };
    },
});

function formElementsModel(props: { companyTypes: KeyValuePair<string, string>[]; countries: CountryModel[]; simpleForm?: boolean; }, labels: IBlockLabels): RequestAccessFormModel {
    const showAddressFormModel = !props.simpleForm;
    const showCompanyRegistrationModel = !props.simpleForm;
    const showCompanyNameWithCompanyType = props.simpleForm;
    const addressFields = showAddressFormModel ? [...addressModel(labels)] : [];
    return [
        headingModel({
            classes: 'mb-25',
            label: labels.contactheadline,
        }),
        ...contactModel(labels, props.simpleForm),
        headingModel({
            classes: 'my-25',
            label: labels.addressheadline,
        }),
        ...addressFields,
        inputSelectModel({
            name: 'address.countryCode',
            validation: 'required',
            label: labels.addresscountrylabel,
            defaultOption: labels.addresscountrychoose,
            options: props.countries,
            valKey: 'code',
            titleKey: 'name',
            displayKey: 'name',
        }),
        headingModel({
            classes: 'my-25',
            label: labels.companyheadline,
        }),
        showCompanyRegistrationModel ? inputTextModel({
            name: 'companyInfo.registrationNumber',
            label: labels.companyvatlabel,
        }) : false,
        showCompanyNameWithCompanyType ? inputTextModel({
            name: 'contactInfo.companyName',
            validation: 'required',
            label: labels.contactcompanylabel,
        }) : false,
        inputSelectModel({
            name: 'companyInfo.companyType',
            validation: 'required',
            label: labels.companytypelabel,
            options: props.companyTypes,
            defaultOption: 'Select type',
            valKey: 'key',
            titleKey: 'value',
            displayKey: 'key',
        }),
        headingModel({
            classes: 'mt-50 mb-15',
            label: labels.commentheadline,
        }),
        textareaModel({
            name: 'comments',
            label: labels.commentlabel,
        }),
    ].filter(Boolean) as RequestAccessFormModel;
}

function generateFormValidation(formElements: RequestAccessFormModel) {
    const formValidation = {};
    formElements.forEach((element) => {
        const elm = element as (InputTextModel | InputSelectModel | TextareaModel | InputTelModel);
        if (elm?.validation) {
            formValidation[elm.name] = elm.validation;
        }
    });

    return formValidation;
}

function addressModel(labels: IBlockLabels): InputTextModel[] {
    return [
        inputTextModel({
            name: 'address.street',
            validation: 'required',
            label: labels.addressstreetlabel,
        }),
        inputTextModel({
            name: 'address.postalCode',
            validation: 'required',
            label: labels.addressziplabel,
        }),
        inputTextModel({
            name: 'address.city',
            validation: 'required',
            label: labels.addresscitylabel,
        }),
        inputTextModel({
            name: 'address.state',
            label: labels.addressregionlabel,
        }),
    ];
}

function contactModel(labels: IBlockLabels, simpleForm?: boolean): (InputTextModel|InputTelModel)[] {
    const showCompanyNameWithContactInfo = !simpleForm;
    const showContactPhone = !simpleForm;
    const showContactWebsite = !simpleForm;
    return [
        showCompanyNameWithContactInfo ? inputTextModel({
            name: 'contactInfo.companyName',
            validation: 'required',
            label: labels.contactcompanylabel,
        }) : false,
        inputTextModel({
            name: 'contactInfo.contactFirstName',
            validation: 'required',
            label: labels.contactfirstnamelabel,
        }),
        inputTextModel({
            name: 'contactInfo.contactLastName',
            validation: 'required',
            label: labels.contactlastnamelabel,
        }),
        showContactPhone ? inputTelModel({
            name: 'contactInfo.contactPhone',
            label: labels.contactphonelabel,
            validation: 'required|phone',
        }) : false,
        inputTextModel({
            name: 'contactInfo.contactEmail',
            validation: 'required|email',
            label: labels.contactemaillabel,
        }),
        showContactWebsite ? inputTextModel({
            name: 'contactInfo.website',
            label: labels.contactwebsitelabel,
        }) : false,
    ].filter(Boolean);
}

function inputTextModel(options: Omit<InputTextModel, 'type'>): InputTextModel {
    return {
        type: 'inputText',
        ...options,
    };
}

function inputTelModel(options: Omit<InputTelModel, 'type'>): InputTelModel {
    return {
        type: 'inputTel',
        ...options,
    };
}

function inputSelectModel(options: Omit<InputSelectModel, 'type'>): InputSelectModel {
    return {
        type: 'select',
        ...options,
    };
}

function headingModel(options: Omit<HeadingModel, 'type'>): HeadingModel {
    return {
        type: 'heading',
        ...options,
    };
}

function textareaModel(options: Omit<TextareaModel, 'type'>): TextareaModel {
    return {
        type: 'textarea',
        ...options,
    };
}
